import React, { useState, useRef, useEffect } from 'react';
import { Input, Select, DatePicker } from 'antd';
import { useLocale } from '@/hooks/useLocale';

const { Option } = Select;

export interface QueryField {
  type: 'select' | 'input' | 'dateRange' | 'preTabInput' | 'customSelect';
  name: string;
  label: string;
  placeholder?: string | string[]; // Allow both string and string array
  options?: { value: string | number | null; label: string }[];
  defaultValue?: any;
  value?: any;
  onChange?: (value: any) => void;
  className?: string;
  // For preTabInput type
  prefixOptions?: { value: string | number; label: string }[];
  prefixValue?: any;
  onPrefixChange?: (value: any) => void;
  prefixPlaceholder?: string;
  // For customSelect type
  customOptions?: { value: string | number; label: string }[];
  customValue?: any;
  onCustomChange?: (value: any) => void;
  customIcon?: string; // iconDown svg or url
  // Custom column span
  colSpan?: {
    mobile?: number; // 1 or 2 for mobile (grid-cols-2)
    desktop?: number; // 1, 2, 3, or 4 for desktop (md:grid-cols-4)
  };
}

export interface GenericQueryCardProps {
  title?: string;
  subtitle?: string;
  button?: React.ReactNode; // Optional button element
  fields: QueryField[];
  onReset?: () => void;
  onQuery?: () => void;
  onExport?: () => void;
  resetButtonText?: string;
  queryButtonText?: string;
  exportButtonText?: string;
  className?: string;
  gridCols?: string; // e.g., "grid-cols-2 md:grid-cols-4"
}

const GenericQueryCard: React.FC<GenericQueryCardProps> = ({
  title,
  subtitle,
  button,
  fields,
  onReset,
  onQuery,
  onExport,
  resetButtonText,
  queryButtonText,
  exportButtonText,
  className = "bg-card-background dark:bg-dark-card-background rounded-lg p-6",
  gridCols = "grid-cols-2 md:grid-cols-4"
}) => {
  const { t } = useLocale();

  // Helper function to get column span classes
  const getColSpanClass = (field: QueryField) => {
    const mobile = field.colSpan?.mobile || 1;
    const desktop = field.colSpan?.desktop || 1;
    const mobileClass = mobile === 2 ? 'col-span-2' : 'col-span-1';
    const desktopClass =
      desktop === 4 ? 'md:col-span-4' :
      desktop === 3 ? 'md:col-span-3' :
      desktop === 2 ? 'md:col-span-2' : 'md:col-span-1';
    return `${mobileClass} ${desktopClass}`;
  };

  // Calculate total grid occupation for button positioning
  const calculateGridOccupation = () => {
    let mobileOccupied = 0;
    let desktopOccupied = 0;
    fields.forEach(field => {
      mobileOccupied += field.colSpan?.mobile || 1;
      desktopOccupied += field.colSpan?.desktop || 1;
    });
    return { mobileOccupied, desktopOccupied };
  };

  // Manage open state for all dropdowns by field name
  const [dropdownOpen, setDropdownOpen] = useState<{ [key: string]: boolean }>({});
  const handleDropdownToggle = (name: string) => {
    setDropdownOpen(prev => ({ ...prev, [name]: !prev[name] }));
  };
  const handleDropdownClose = (name: string) => {
    setDropdownOpen(prev => ({ ...prev, [name]: false }));
  };

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      setDropdownOpen({});
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  const renderField = (field: QueryField) => {
    // Standardized style for all fields
    const baseClassName = "h-8 w-full bg-card-background dark:bg-dark-card-background border border-normal dark:border-dark-normal rounded-md text-xs font-inter font-normal text-sel-normal dark:text-dark-sel-normal leading-none px-2 flex items-center focus:outline-none focus:ring-0 placeholder:text-normal dark:placeholder:text-dark-normal";

    switch (field.type) {
      case 'select': {
        const selectedLabel = (field.options || []).find(opt => opt.value === field.value)?.label;
        return (
          <div className={`relative w-full ${field.className || ''}`} onClick={e => e.stopPropagation()}>
            <div
              className={
                `flex items-center cursor-pointer h-8 px-2 bg-card-background dark:bg-dark-card-background border border-normal dark:border-dark-normal rounded-md w-full`
              }
              onClick={() => handleDropdownToggle(field.name)}
            >
              <div className="flex-1 text-xs font-inter font-normal text-sel-normal dark:text-dark-sel-normal truncate">
                {selectedLabel || (typeof field.placeholder === 'string' ? field.placeholder : '')}
              </div>
              <div className="ml-1 flex items-center">
                <svg className={`h-3 w-3 transition-transform duration-300 ${dropdownOpen[field.name] ? 'rotate-180' : ''}`} viewBox="0 0 12 8" fill="none"><path d="M1 1.5L6 6.5L11 1.5" stroke="#A1A3C4" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </div>
            </div>
            <div className={`absolute left-0 mt-1 p-[10px] bg-button dark:bg-dark-button rounded-md shadow-lg z-50 min-w-full transition-all border border-normal duration-300 origin-top inline-flex flex-col justify-start items-start ${dropdownOpen[field.name] ? 'opacity-100 scale-y-100 transform' : 'opacity-0 scale-y-0 transform pointer-events-none'}`}> 
              {(field.options || []).map(option => (
                <div
                  key={option.value}
                  className={`self-stretch h-6 py-1 inline-flex justify-start items-center cursor-pointer hover:bg-button-hover dark:hover:bg-dark-button-hover ${option.value === field.value ? 'text-sel-normal dark:text-dark-sel-normal' : 'text-normal dark:text-dark-normal'}`}
                  onClick={e => {
                    e.stopPropagation();
                    field.onChange?.(option.value);
                    handleDropdownClose(field.name);
                  }}
                >
                  <div className="justify-start text-xs font-inter font-normal leading-tight truncate">
                    {option.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      }

      case 'input':
        return (
          <input
            id={`input-${field.name}`}
            className={`${baseClassName} ${field.className || ''}`}
            defaultValue={field.defaultValue}
            value={field.value}
            style={{ width: '100%', height: '2rem', background: 'transparent', borderRadius: '0.375rem', fontSize: '0.75rem', fontFamily: 'Inter, sans-serif', color: 'inherit' }}
            onChange={(e) => field.onChange?.(e.target.value)}
            placeholder={typeof field.placeholder === 'string' ? field.placeholder : undefined}
            type="text"
          />
        );

      case 'preTabInput': {
        const selectedLabel = (field.prefixOptions || []).find(opt => opt.value === field.prefixValue)?.label;
        const selectBefore = (
          <div className="z-10" onClick={e => e.stopPropagation()}>
            <div
              className={
                `flex items-center cursor-pointer h-7 px-2 bg-card-background dark:bg-dark-card-background rounded-l-md focus:outline-none`
              }
              style={{ minWidth: 0 }}
              onClick={() => handleDropdownToggle(field.name + '-prefix')}
            >
              <div className="flex-1 text-xs font-inter font-normal text-sel-normal dark:text-dark-sel-normal truncate">
                {selectedLabel || (typeof field.prefixPlaceholder === 'string' ? field.prefixPlaceholder : '')}
              </div>
              <div className="ml-1 flex items-center">
                {field.customIcon ? (
                  <img
                    src={field.customIcon}
                    alt="dropdown arrow"
                    className={`h-3 w-3 transition-transform duration-300 ${dropdownOpen[field.name + '-prefix'] ? 'rotate-180' : ''}`}
                  />
                ) : (
                  <svg className={`h-3 w-3 transition-transform duration-300 ${dropdownOpen[field.name + '-prefix'] ? 'rotate-180' : ''}`} viewBox="0 0 12 8" fill="none"><path d="M1 1.5L6 6.5L11 1.5" stroke="#A1A3C4" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                )}
              </div>
            </div>
            <div className={`absolute left-0 mt-1 p-[10px] bg-button dark:bg-dark-button rounded-md shadow-lg z-30 min-w-full transition-all border border-normal duration-300 origin-top inline-flex flex-col justify-start items-start ${dropdownOpen[field.name + '-prefix'] ? 'opacity-100 scale-y-100 transform' : 'opacity-0 scale-y-0 transform pointer-events-none'}`}>
              {(field.prefixOptions || []).map(option => (
                <div
                  key={option.value}
                  className={`self-stretch h-6 py-1 inline-flex justify-start items-center cursor-pointer hover:bg-button-hover dark:hover:bg-dark-button-hover ${option.value === field.prefixValue ? 'text-sel-normal dark:text-dark-sel-normal' : 'text-normal dark:text-dark-normal'}`}
                  onClick={e => {
                    e.stopPropagation();
                    field.onPrefixChange?.(option.value);
                    handleDropdownClose(field.name + '-prefix');
                  }}
                >
                  <div className="justify-start text-xs font-inter font-normal leading-tight truncate">
                    {option.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
        return (
          <div className={`flex flex-row items-center h-8 border border-normal dark:border-dark-normal rounded-md bg-card-background dark:bg-dark-card-background relative w-full ${field.className || ''}`}> 
            {selectBefore}
            {/* vertical divider */}
            <div className="w-px h-6 bg-button-hover dark:bg-dark-button-hover mx-0" />
            <input
              id={`preTabInput-I-${field.name}`}
              className={
                `w-full h-8 px-2 bg-transparent text-sel-normal dark:text-dark-sel-normal text-xs font-inter font-normal border-0 focus:ring-0 focus:outline-none placeholder:text-normal dark:placeholder:text-dark-normal rounded-r-md`
              }
              defaultValue={field.defaultValue}
              value={field.value}
              style={{ width: '100%', background: 'transparent', boxShadow: 'none' }}
              onChange={(e) => field.onChange?.(e.target.value)}
              placeholder={typeof field.placeholder === 'string' ? field.placeholder : undefined}
              type="text"
            />
          </div>
        );
      }
      
      case 'dateRange':
        return (
            <DatePicker.RangePicker
                id={`dateRange-${field.name}`}
                className={`query-selector ${baseClassName} ${field.className || ''}`}
                value={field.value}
                onChange={field.onChange}
                placeholder={Array.isArray(field.placeholder) ? field.placeholder as [string, string] : undefined}
                format={'YYYY-MM-DD'}
                showTime={false}
                allowClear={false}
                size={'middle'}
                // popupClassName={`custom-date-picker-dropdown`}
                placement={'bottomLeft'}
                getPopupContainer={() => document.body}
                style={{ width: '100%', height: '2rem', background: 'transparent', borderRadius: '0.375rem', fontSize: '0.75rem', fontFamily: 'Inter, sans-serif', color: 'inherit' }}
                // presets={[
                //     { label: t('calendar.all'), value: [null, null] },
                //     { label: t('calendar.today'), value: [dayjs(), dayjs()] },
                //     { label: t('calendar.yesterday'), value: [dayjs().subtract(1, 'day'), dayjs().subtract(1, 'day')] },
                //     { label: t('calendar.last7Days'), value: [dayjs().subtract(6, 'day'), dayjs()] },
                //     { label: t('calendar.last30Days'), value: [dayjs().subtract(29, 'day'), dayjs()] },
                // ]}
            />
        );
      
      default:
        return null;
    }
  };

  // Calculate button positioning logic
  const getButtonPosition = () => {
    const { mobileOccupied, desktopOccupied } = calculateGridOccupation();
    const isMobile = gridCols.includes('grid-cols-2');
    const isDesktop = gridCols.includes('md:grid-cols-4');
    
    if (isMobile && isDesktop) {
      // Mobile: 2 columns max, Desktop: 4 columns max
      const mobileRemainingInLastRow = mobileOccupied % 2;
      const mobileBtnInSameRow = mobileRemainingInLastRow !== 0 && mobileOccupied < 2;
      
      const desktopRemainingInLastRow = desktopOccupied % 4;
      const desktopBtnInSameRow = desktopRemainingInLastRow !== 0 && desktopOccupied < 4;
      
      if (desktopBtnInSameRow && mobileBtnInSameRow) {
        // Both mobile and desktop have space in same row
        const desktopSpanLeft = 4 - desktopRemainingInLastRow;
        const mobileSpanLeft = 2 - mobileRemainingInLastRow;
        return `items-end col-span-${mobileSpanLeft} md:col-span-${desktopSpanLeft} flex flex-wrap flex-col md:flex-row justify-end gap-2`;
      } else if (desktopBtnInSameRow && !mobileBtnInSameRow) {
        // Desktop has space, mobile needs new row
        const desktopSpanLeft = 4 - desktopRemainingInLastRow;
        return `items-end col-span-2 md:col-span-${desktopSpanLeft} flex flex-wrap flex-row justify-end gap-2`;
      } else if (!desktopBtnInSameRow && mobileBtnInSameRow) {
        // Mobile has space, desktop needs new row
        const mobileSpanLeft = 2 - mobileRemainingInLastRow;
        return `items-end col-span-${mobileSpanLeft} md:col-span-4 flex flex-wrap flex-col md:flex-row justify-end gap-2`;
      } else {
        // Both need new row
        // Fix: If desktopRemainingInLastRow is not 0, span only the remaining columns, else span all 4
        const desktopSpanLeft = desktopRemainingInLastRow === 0 ? 4 : 4 - desktopRemainingInLastRow;
        // If the button group is in the last 2 columns, add md:col-start-3
        const colStart = desktopSpanLeft === 2 ? 'md:col-start-3' : '';
        return `items-end col-span-2 md:col-span-${desktopSpanLeft} ${colStart} flex flex-wrap flex-row justify-end gap-2`;
      }
    }
    
    // Fallback for other grid configurations
    return "items-end col-span-2 md:col-span-4 flex flex-wrap justify-end gap-2";
  };

  return (
    <div>
      {/* Header Section */}
      {(title || subtitle || button) && (
        <div className="flex gap-[10px] items-center pb-6">
          {title && (
            <span className="text-xl font-normal font-inter leading-normal text-sel-normal dark:text-dark-sel-normal">
              {title}
            </span>
          )}
          {subtitle && (
            <span className="text-xs font-normal font-inter leading-normal text-normal dark:text-dark-normal">
              {subtitle}
            </span>
          )}
          {button && (
              <span>{button}</span>
          )}
        </div>
      )}
      
      {/* Query Card */}
      <div className={className}>
        <div className={`grid ${gridCols} gap-6`}>
          {/* Render Fields */}
          {fields.map((field, index) => (
            <div key={field.name || index} className={`flex flex-col gap-[6px] ${getColSpanClass(field)}`}>
              <div className="min-h-[14px] text-sel-normal dark:text-dark-sel-normal text-xs font-normal font-inter leading-none">
                {field.label}
              </div>
              {renderField(field)}
            </div>
          ))}
          
          {/* Buttons - Dynamic positioning based on field count */}
          <div className={getButtonPosition()}>
            {onExport && (
              <button 
                className="h-8 bg-card-background dark:bg-dark-card-background border border-dark-main dark:border-main text-sel-normal dark:text-dark-sel-normal opacity-60 hover:opacity-50 text-xs font-normal font-inter leading-tight px-4 py-1.5 rounded whitespace-nowrap"
                onClick={onExport}
              >
                {exportButtonText || t('dataAnalysis.dataOverview.export')}
              </button>
            )}
            {onReset && (
              <button 
                className="h-8 bg-card-background dark:bg-dark-card-background border border-dark-main dark:border-main text-sel-normal dark:text-dark-sel-normal opacity-60 hover:opacity-50 text-xs font-normal font-inter leading-tight px-4 py-1.5 rounded whitespace-nowrap"
                onClick={onReset}
              >
                {resetButtonText || t('dataAnalysis.dataOverview.reset')}
              </button>
            )}
            {onQuery && (
              <button 
                className="h-8 bg-tabline dark:bg-dark-tabline hover:bg-hover-bg dark:hover:bg-dark-hover-bg text-sel-normal dark:text-dark-sel-normal text-xs font-normal font-inter leading-tight px-4 py-1.5 rounded whitespace-nowrap"
                onClick={onQuery}
              >
                {queryButtonText || t('dataAnalysis.dataOverview.query')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GenericQueryCard;